import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";
import { Plus } from "@phosphor-icons/react";
import {
  Avatar,
  Button,
  Dialog,
  Flex,
  IconButton,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";

import { useUI } from "../../../contexts/ui";
import { useSearchEnterpriseUsers } from "../../../utils/queries/askPuntt";
import { useAddProjectMembers } from "../../../utils/queries/askPunttProjects";
import { sortEnterpriseUsers } from "../../../utils/sortEnterpriseUsers";

/**
 * Owner-facing "+ Invite" modal for an Ask Puntt project. Searches enterprise
 * users (server-side) and adds them to the project's member allowList via
 * `addProjectMembers`. Mirrors ShareAskPunttChatDialog but scoped to projects.
 */
export function ProjectInviteDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  members,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName?: string;
  members: AskPunttChatUserSummary[];
}) {
  const { notify } = useUI();
  const [rawQuery, setRawQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(rawQuery), 250);
    return () => window.clearTimeout(handle);
  }, [rawQuery]);

  useEffect(() => {
    if (open) {
      setRawQuery("");
      setDebouncedQuery("");
    }
  }, [open]);

  const trimmedQuery = debouncedQuery.trim();
  const search = useSearchEnterpriseUsers(trimmedQuery, {
    enabled: open && trimmedQuery.length >= 1,
  });
  const addMembers = useAddProjectMembers(projectId);

  const memberIds = useMemo(
    () => new Set(members.map((m) => m._id)),
    [members],
  );

  const candidates = useMemo(
    () =>
      sortEnterpriseUsers(
        (search.data?.users ?? []).filter((u) => !memberIds.has(u._id)),
      ),
    [search.data, memberIds],
  );

  const handleAdd = async (user: AskPunttChatUserSummary) => {
    try {
      await addMembers.mutateAsync([user._id]);
      notify({
        title: "Invited",
        message: `Added ${user.name || user.email || "user"} to the project.`,
      });
      setRawQuery("");
      setDebouncedQuery("");
    } catch {
      // useAddProjectMembers shows an error toast; keep the dialog open.
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="500px"
        data-testid="project-invite-dialog"
        aria-labelledby="project-invite-title"
      >
        <Dialog.Title id="project-invite-title">Invite to project</Dialog.Title>
        <Dialog.Description size="2" mb="3">
          {projectName
            ? `Add people in your organization to "${projectName}". They'll see the project feed, reviews, and shared chats.`
            : "Add people in your organization to this project."}
        </Dialog.Description>

        <Text
          as="label"
          size="2"
          weight="medium"
          htmlFor="project-invite-search-input"
        >
          Search people
        </Text>
        <TextField.Root
          id="project-invite-search-input"
          data-testid="project-invite-search-input"
          placeholder="Search by name or email"
          value={rawQuery}
          onChange={(e) => setRawQuery(e.target.value)}
          mt="1"
          autoComplete="off"
        />

        {trimmedQuery.length >= 1 ? (
          <Flex
            direction="column"
            gap="1"
            mt="2"
            data-testid="project-invite-results"
            role="list"
            aria-label="Search results"
          >
            {search.isLoading ? (
              <Text
                size="2"
                color="gray"
                data-testid="project-invite-results-loading"
              >
                Searching…
              </Text>
            ) : candidates.length === 0 ? (
              <Text
                size="2"
                color="gray"
                data-testid="project-invite-results-empty"
              >
                No new matches for &quot;{trimmedQuery}&quot;.
              </Text>
            ) : (
              candidates.map((user) => (
                <Flex
                  key={user._id}
                  align="center"
                  gap="2"
                  data-testid={`project-invite-result-${user._id}`}
                  role="listitem"
                  className="rounded-md px-2 py-1.5 hover:bg-puntt-neutral-gray-3"
                >
                  <Avatar
                    src={user.avatar ?? undefined}
                    fallback={(user.name || user.email || "?")
                      .charAt(0)
                      .toUpperCase()}
                    size="1"
                    radius="full"
                  />
                  <Flex direction="column" className="min-w-0 flex-1">
                    <Text size="2" weight="medium" className="truncate">
                      {user.name?.trim() || user.email?.trim() || user._id}
                    </Text>
                    {user.name && user.email ? (
                      <Text size="1" color="gray" className="truncate">
                        {user.email}
                      </Text>
                    ) : null}
                  </Flex>
                  <IconButton
                    data-testid={`project-invite-add-${user._id}`}
                    aria-label={`Invite ${user.name || user.email || "user"}`}
                    variant="soft"
                    size="1"
                    disabled={addMembers.isPending}
                    onClick={() => handleAdd(user)}
                  >
                    <Plus size={14} aria-hidden="true" />
                  </IconButton>
                </Flex>
              ))
            )}
          </Flex>
        ) : null}

        <Flex direction="column" gap="1" mt="4">
          <Text size="2" weight="medium" id="project-invite-members-heading">
            Members
          </Text>
          {members.length === 0 ? (
            <Text
              size="2"
              color="gray"
              data-testid="project-invite-members-empty"
            >
              Just you for now. Search above to invite teammates.
            </Text>
          ) : (
            <Flex
              direction="column"
              gap="1"
              role="list"
              aria-labelledby="project-invite-members-heading"
            >
              {members.map((user) => (
                <Flex
                  key={user._id}
                  align="center"
                  gap="2"
                  data-testid={`project-invite-member-${user._id}`}
                  role="listitem"
                  className="rounded-md px-2 py-1.5"
                >
                  <Avatar
                    src={user.avatar ?? undefined}
                    fallback={(user.name || user.email || "?")
                      .charAt(0)
                      .toUpperCase()}
                    size="1"
                    radius="full"
                  />
                  <Text size="2" weight="medium" className="truncate">
                    {user.name?.trim() || user.email?.trim() || user._id}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>

        <Flex mt="4" justify="end">
          <Button
            variant="soft"
            color="gray"
            data-testid="project-invite-done-button"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
